import client from './client';
import type { CookedResult, Menu, PaginatedResponse, ShoppingList } from '../types';

export interface DeletedMenu {
  id: number;
  menu_id: number;
  data: any;
  deleted_by_name: string | null;
  deleted_at: string;
  purge_after: string;
  can_purge: boolean;
}

export interface SwapResult {
  allergen_warning: boolean;
  allergens_found: string[];
  calorie_warning: boolean;
  recipe_calories: number;
  // MG_SWAPFREE: замена на блюдо другой пищевой группы разрешена, но меняет
  // баланс меню — бэкенд об этом предупреждает, а не запрещает.
  food_group_warning?: boolean;
  food_group_expected?: string;
  food_group_new?: string;
}

// MG_607_V_api: расширенный generate
export interface GenerateMenuPayload {
  period_days: number;
  start_date: string;
  meal_plan_type?: '3' | '5';
  strategy?: '1' | '2' | '3';      // MG_STRAT_WEB: 1=стандарт, 2=состав, 3=тарелка
  country?: string;                 // legacy single
  countries?: string[];             // MG-607: мульти
  max_cook_time?: number;
  exclude_allergens?: string[];     // MG-607: [] = выкл фильтр; undefined = из profile
  exclude_disliked?: string[];      // MG-607: то же
  member_ids?: number[];
  mode?: 'per_member' | 'family';
  with_soup?: boolean;             // MG_610_V_web: include soup in lunch
}

export const menuApi = {
  // MG_MENUEXPIRE: по умолчанию — актуальные (срок ещё не вышел),
  // archived: true — те, чей срок прошёл или кого пометили архивом.
  list: (archived = false) =>
    client.get<PaginatedResponse<Menu>>('/menu/', archived ? { params: { archived: 'true' } } : undefined),

  get: (id: number) => client.get<Menu>(`/menu/${id}/`),

  generate: (data: GenerateMenuPayload) => client.post<Menu>('/menu/generate/', data),

  delete: (id: number) => client.delete(`/menu/${id}/delete/`),

  archive: (id: number) => client.post(`/menu/${id}/archive/`),

  quarantine: () => client.get<DeletedMenu[]>('/menu/quarantine/'),

  restore: (deletedId: number) => client.post<Menu>(`/menu/quarantine/${deletedId}/restore/`),

  // MG_608_V_api
  purge: (deletedId: number) => client.delete(`/menu/quarantine/${deletedId}/purge/`),

  purgeAll: () => client.delete<{ deleted: number }>(`/menu/quarantine/purge-all/`),

  // MG_WRITEOFF: «приготовил» — списать продукты блюда из холодильника.
  // Повторный вызов ничего не меняет и отдаёт 200 с уже сделанным событием;
  // по 201 клиент отличает первое нажатие от повторного (поле created).
  cookItem: (menuId: number, itemId: number) =>
    client.post<CookedResult>(`/menu/${menuId}/items/${itemId}/cooked/`),

  // Отмена: возвращает в холодильник ровно то, что ушло.
  undoCookItem: (menuId: number, itemId: number) =>
    client.delete(`/menu/${menuId}/items/${itemId}/cooked/`),

  swapItem: (menuId: number, itemId: number, recipeId: number) =>
    client.patch<SwapResult>(`/menu/${menuId}/items/${itemId}/`, { recipe_id: recipeId }),

  // MG_ITEMDEL: убрать блюдо из меню. 409 — блюдо отмечено приготовленным,
  // продукты уже списаны; текст причины приходит в detail.
  deleteItem: (menuId: number, itemId: number) =>
    client.delete(`/menu/${menuId}/items/${itemId}/`),

  shoppingList: (menuId: number) =>
    client.get<ShoppingList>(`/menu/${menuId}/shopping-list/`),

  toggleShoppingItem: (menuId: number, itemId: number) =>
    client.patch(`/menu/${menuId}/shopping-list/items/${itemId}/toggle/`),
};

// MG-402
export async function swapMenuItem(menuId: number, itemId: number, recipeId: number) {
  const { data } = await client.patch<SwapResult>(
    `/menu/${menuId}/items/${itemId}/`, { recipe_id: recipeId }
  );
  return data;
}

// MG_PRODDISH: заменить позицию меню на ПРОДУКТ (с порцией в граммах).
export async function swapMenuItemProduct(
  menuId: number, itemId: number, productId: number, grams: number,
) {
  const { data } = await client.patch<SwapResult>(
    `/menu/${menuId}/items/${itemId}/`, { product_id: productId, grams },
  );
  return data;
}
